import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButton from 'webapp/components/submit-button';

interface Item {
	icon: IconName;
	text: string;
}

export default class OnboardingPopupGenieArtifacts extends PopupInnerModalComponent<PopupData.None> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	title = 'Turn your analysis into a shareable report';
	description =
		'Ask Genie a question, refine the answer with follow-ups, then package the conversation into a polished report, deck, or interactive doc.';

	items: Item[] = [
		{ icon: 'Ai', text: 'Built from your full conversation' },
		{ icon: 'Brush', text: "Match your brand or your client's" },
		{ icon: 'Share', text: 'Share by link or PDF' },
	];

	get imgSrc() {
		return `/images/onboarding/genie-artifacts/report-${this.navigationService.theme.id}.png`;
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// Writing user settings during construction dirties the tracked `userSettings`
		// mid-render and trips Ember's backtracking-rerender assertion — defer past render.
		next(() => {
			this.userSettingsService.hasSeenOnboardingGenieArtifactsPopup = true;
			this.analyticsService.event(TrackingEvent.GENIE_ARTIFACTS_MODAL_SHOWN);
		});
	}

	@action
	startBuilding() {
		this.analyticsService.event(TrackingEvent.GENIE_ARTIFACTS_MODAL_CTA_CLICKED);
		this.navigationService.transitionToRoute('genie');
		this.close();
	}

	<template>
		<div class="content">
			<div class="content__left">
				<div class="text">
					<div class="text__header">
						<span class="badge2 badge2--blue">NEW</span>
						<h3>{{this.title}}</h3>
					</div>
					<p>{{this.description}}</p>
				</div>

				<ul>
					{{#each this.items as |item|}}
						<li>
							<CommonUiIconComponent @name={{item.icon}} @size="md" />
							<span>{{item.text}}</span>
						</li>
					{{/each}}
				</ul>

				<div class="controls2">
					<div class="controls2__left">
						<SubmitButton @text="Start building" @onTrigger={{this.startBuilding}} />
					</div>
				</div>
			</div>
			<div class="content__right">
				<img class="image" src={{this.imgSrc}} alt="" />
			</div>
		</div>
	</template>
}
